/**
 * activeModules — UPT-2069 (L1) : la source UNIQUE de « ce module est-il ouvert (actif ou en essai) pour le
 * locataire courant ? » côté web, et la normalisation unique des codes de module.
 *
 * Décision : l'auth-context servi par le serveur (`moduleEntitlements`, statuts ACTIVE / TRIAL, règle
 * `ModuleActivationStatus.isEntitled()` du rest). Vocabulaire : `moduleScopes` (code canonique, domaines, alias,
 * clés de menu et d'entité, tirés de `ModuleScopeResolver`) et, à défaut, le domaine des modules ouverts
 * (`modules[].domain`). Plus aucune table d'alias ni registre codé en dur côté web.
 *
 * Avant l'arrivée de l'auth-context (premier rendu), le snapshot `localStorage` `tenant_module_entitlements` sert de
 * CACHE D'AFFICHAGE seulement : un module qu'il déclare actif est montré, mais un snapshot vide ou absent vaut
 * « aucun module ouvert » — il ne « laisse plus tout passer ». Dès que l'auth-context est là, il décide seul.
 *
 * Hook réactif : `useActiveModules()` (hooks/useActiveModules.ts). Rechargement après une action de la Marketplace :
 * `reloadAuthContext()` (api/reloadAuthContext.ts).
 */
import type {
  AuthContextModuleEntitlement,
  AuthContextResponse,
  ModuleDefinitionDTO,
  ModuleScopeDTO,
} from '@/types/auth_context';
import { getAuthContextState } from '@/uptk/core/api/auth_context_store';
import { loadModuleEntitlementSnapshot } from '@/uptk/core/entitlements/module_entitlement_storage';
import { normalizeModuleKey } from './moduleKey';

/** Statuts qui ouvrent un module (règle « actif ou essai », décision 4 du 08.10). */
const OPEN_STATUSES = new Set(['ACTIVE', 'TRIAL']);

/** Le module socle, toujours ouvert (il porte le portail lui-même). */
export const CORE_MODULE_CODE = 'core';

/** Modules socles (le CORE et l'administration) : jamais « fermés » pour un locataire, jamais « de module ». */
export const BASE_MODULE_CODES: ReadonlySet<string> = new Set([CORE_MODULE_CODE, 'admin']);

export type ActiveModuleRef = {
  /** Code tel que servi par le serveur (`tenant_modules.module_code`), en minuscules. */
  code: string;
  label: string;
  status: 'ACTIVE' | 'TRIAL';
  /** Code canonique (`crisis-management` → `crisis`) ; égal à `code` sans vocabulaire serveur. */
  canonicalCode: string;
  domain?: string;
  /** Domaine de route puis domaines d'entités. */
  domains: string[];
  aliases: string[];
};

type ContextInput = Pick<AuthContextResponse, 'moduleEntitlements' | 'modules' | 'moduleScopes'> | null | undefined;

export { normalizeModuleKey };

/** Forme compacte (lettres et chiffres) : seulement pour relire un ancien snapshot aux clés `FINANCEADDONPMO`. */
const compactKey = (value: unknown): string => normalizeModuleKey(value).replace(/[^a-z0-9]/g, '');

const uniq = (values: Array<string | null | undefined>): string[] => {
  const out: string[] = [];
  values.forEach((value) => {
    const key = normalizeModuleKey(value);
    if (key && !out.includes(key)) out.push(key);
  });
  return out;
};

/** Portées servies par le serveur, normalisées (vide pour un serveur antérieur à L1). */
export function moduleScopesOf(context: ContextInput = getAuthContextState()): Required<ModuleScopeDTO>[] {
  const scopes = Array.isArray(context?.moduleScopes) ? context!.moduleScopes : [];
  return scopes
    .filter((scope): scope is ModuleScopeDTO => Boolean(scope) && typeof scope.code === 'string')
    .map((scope) => {
      const code = normalizeModuleKey(scope.code);
      const domain = normalizeModuleKey(scope.domain ?? '');
      return {
        code,
        canonicalCode: normalizeModuleKey(scope.canonicalCode) || code,
        domain: domain || null,
        domains: uniq([domain, ...(scope.domains ?? [])]),
        aliases: uniq(scope.aliases ?? []),
        keys: uniq(scope.keys ?? []),
      };
    })
    .filter((scope) => scope.code.length > 0);
}

/**
 * Le module DÉFINI que désigne un code, un alias ou un domaine — sans le ramener à son canonique : un code défini
 * reste lui-même (`crisis-management`), un alias va à son module (`financial-addon-pmo` → `finance-addon-pmo`,
 * `bcm` → `business-component-modeling`), un domaine au module qui le porte (`pmo` → `finance-addon-pmo`, domaine
 * de route d'abord, module canonique avant l'alias). Un code inconnu revient normalisé, tel quel. Sert aux URL
 * courtes des tableaux de bord de module (ex-`moduleCodeAliases.ts`).
 */
export function resolveModuleCode(value: unknown, context: ContextInput = getAuthContextState()): string {
  const key = normalizeModuleKey(value);
  if (!key) return key;
  const scopes = moduleScopesOf(context);
  if (scopes.some((scope) => scope.code === key)) return key;
  const byAlias = scopes.find((scope) => scope.aliases.includes(key));
  if (byAlias) return byAlias.code;
  const byRouteDomain = scopes.filter((scope) => scope.domain === key);
  const owner =
    byRouteDomain.find((scope) => scope.canonicalCode === scope.code) ??
    byRouteDomain[0] ??
    scopes.find((scope) => scope.domains.includes(key));
  if (owner) return owner.code;
  // Sans vocabulaire serveur : le domaine d'un module ouvert (modules[].domain) reste reconnu.
  const definitions: ModuleDefinitionDTO[] = Array.isArray(context?.modules) ? context!.modules : [];
  const byDefinitionDomain = definitions.find((definition) => normalizeModuleKey(definition.domain) === key);
  return byDefinitionDomain ? normalizeModuleKey(byDefinitionDomain.code) : key;
}

/**
 * Code canonique d'un code, d'un alias ou d'un domaine : `crisis-management` → `crisis` (décision 5),
 * `financial-addon-pmo` → `finance-addon-pmo`, `pmo` → `finance-addon-pmo`. Un code inconnu revient normalisé.
 */
export function canonicalModuleCode(value: unknown, context: ContextInput = getAuthContextState()): string {
  const resolved = resolveModuleCode(value, context);
  if (!resolved) return resolved;
  const scope = moduleScopesOf(context).find((candidate) => candidate.code === resolved);
  return scope ? scope.canonicalCode : resolved;
}

/** Modules ouverts de l'auth-context (ACTIVE ou TRIAL), enrichis de leur vocabulaire, triés par libellé. */
export function readActiveModules(context: ContextInput = getAuthContextState()): ActiveModuleRef[] {
  const entitlements: AuthContextModuleEntitlement[] = Array.isArray(context?.moduleEntitlements)
    ? context!.moduleEntitlements
    : [];
  const scopes = moduleScopesOf(context);
  const definitions: ModuleDefinitionDTO[] = Array.isArray(context?.modules) ? context!.modules : [];
  return entitlements
    .filter((entitlement) => entitlement && OPEN_STATUSES.has(String(entitlement.status ?? '').toUpperCase()))
    .map((entitlement) => {
      const code = normalizeModuleKey(entitlement.moduleCode);
      // Un code hérité (alias déclaré par un manifeste) prend le vocabulaire du module qu'il désigne.
      const resolved = resolveModuleCode(code, context);
      const scope =
        scopes.find((candidate) => candidate.code === code) ?? scopes.find((candidate) => candidate.code === resolved);
      const definition = definitions.find((candidate) => normalizeModuleKey(candidate.code) === code);
      const domain = scope?.domain ?? (normalizeModuleKey(definition?.domain) || undefined);
      return {
        code,
        label: entitlement.moduleName || entitlement.moduleCode,
        status: String(entitlement.status).toUpperCase() === 'TRIAL' ? ('TRIAL' as const) : ('ACTIVE' as const),
        canonicalCode: scope?.canonicalCode ?? code,
        domain: domain ?? undefined,
        domains: uniq([domain, ...(scope?.domains ?? [])]),
        aliases: scope?.aliases ?? [],
      };
    })
    .filter((module) => module.code.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Vrai quand `code` désigne l'un des modules ouverts : son code réel (`finance-addon-pmo`), son code canonique, l'un
 * de ses alias (`financial-addon-pmo`, `crisis-management` ⇄ `crisis`) ou l'un de ses domaines (`pmo`, `iso22301`).
 */
export function isModuleCodeActive(
  modules: ActiveModuleRef[],
  code: string | null | undefined,
  context: ContextInput = getAuthContextState(),
): boolean {
  const wanted = normalizeModuleKey(code);
  if (!wanted) return false;
  if (wanted === CORE_MODULE_CODE) return true;
  const canonical = canonicalModuleCode(wanted, context);
  return modules.some(
    (module) =>
      module.code === wanted ||
      module.canonicalCode === canonical ||
      module.aliases.includes(wanted) ||
      module.domains.includes(wanted),
  );
}

/** Codes ouverts selon le snapshot `localStorage` (cache de premier rendu) ; vide sans snapshot. */
function snapshotOpenCodes(tenantId?: string): string[] {
  const snapshot = loadModuleEntitlementSnapshot(tenantId);
  if (!snapshot) return [];
  return Object.entries(snapshot.entitlements ?? {})
    .filter(([, record]) => Boolean(record?.isActive))
    .map(([key, record]) => compactKey(record?.moduleCode || key))
    .filter(Boolean);
}

export type ModuleOpenOptions = {
  context?: ContextInput;
  tenantId?: string;
};

/**
 * LA question « ce module est-il ouvert ? » hors composant React. Auth-context chargé : lui seul décide. Sinon,
 * premier rendu : le snapshot ne sert que de cache (code exact) ; snapshot vide ou absent = fermé.
 */
export function isModuleOpen(code: string | null | undefined, options: ModuleOpenOptions = {}): boolean {
  const wanted = normalizeModuleKey(code);
  if (!wanted) return false;
  if (wanted === CORE_MODULE_CODE) return true;
  const context = options.context === undefined ? getAuthContextState() : options.context;
  if (context) {
    return isModuleCodeActive(readActiveModules(context), wanted, context);
  }
  return snapshotOpenCodes(options.tenantId).includes(compactKey(wanted));
}

/**
 * Modules propriétaires d'une rubrique, d'un site ou d'une clé de menu (`finance`, `project`, `budget`) : d'abord
 * par code, alias ou domaine, sinon par clé de menu ou code d'entité. Vide = clé de plateforme (aucun module).
 */
export function moduleOwnersOfKey(key: string | null | undefined, context: ContextInput = getAuthContextState()): string[] {
  const wanted = normalizeModuleKey(key);
  if (!wanted) return [];
  const scopes = moduleScopesOf(context);
  const byVocabulary = scopes.filter(
    (scope) => scope.code === wanted || scope.aliases.includes(wanted) || scope.domains.includes(wanted),
  );
  const owners = byVocabulary.length > 0 ? byVocabulary : scopes.filter((scope) => scope.keys.includes(wanted));
  return uniq(owners.map((scope) => scope.code));
}

/**
 * Une rubrique est-elle ouverte ? `undefined` quand aucun module connu ne la porte (rubrique de plateforme : à
 * l'appelant de décider) ; sinon vrai si l'un de ses modules propriétaires est ouvert (`core` compris).
 */
export function isModuleKeyOpen(key: string | null | undefined, options: ModuleOpenOptions = {}): boolean | undefined {
  const context = options.context === undefined ? getAuthContextState() : options.context;
  const owners = moduleOwnersOfKey(key, context);
  if (owners.length === 0) return undefined;
  return owners.some((owner) => isModuleOpen(owner, { ...options, context }));
}

/**
 * Code du module OUVERT désigné par un alias d'URL (`/portal/{ws}/{année}/cbm/dashboard`, `/portal/finance`) : code
 * exact, alias, domaine, puis — repli historique UPT-1853 — module dont le code commence par `<alias>-` (socle avant
 * add-on). `null` si aucun module ouvert ne correspond.
 */
export function resolveActiveModuleCode(
  alias: string | null | undefined,
  modules: ActiveModuleRef[],
  context: ContextInput = getAuthContextState(),
): string | null {
  const wanted = normalizeModuleKey(alias);
  if (!wanted) return null;
  const exact = modules.find((module) => module.code === wanted);
  if (exact) return exact.code;
  const canonical = canonicalModuleCode(wanted, context);
  const byVocabulary =
    modules.find((module) => module.canonicalCode === canonical && module.code === canonical) ??
    modules.find((module) => module.canonicalCode === canonical) ??
    modules.find((module) => module.aliases.includes(wanted)) ??
    modules.find((module) => module.domain === wanted) ??
    modules.find((module) => module.domains.includes(wanted));
  if (byVocabulary) return byVocabulary.code;
  const prefixed = modules
    .map((module) => module.code)
    .filter((code) => code.startsWith(`${wanted}-`))
    .sort((a, b) => {
      const addonA = a.includes('-addon-') ? 1 : 0;
      const addonB = b.includes('-addon-') ? 1 : 0;
      return addonA - addonB || a.localeCompare(b);
    });
  return prefixed[0] ?? null;
}
